"use client";

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Alert, Skeleton } from "@offstreetparking/design-system/feedback";
import { Button } from "@offstreetparking/design-system/forms";
import { AddIcon } from "@offstreetparking/design-system/icons/AddIcon";
import { VehicleIcon } from "@offstreetparking/design-system/icons/VehicleIcon";
import type { CountryValue } from "@offstreetparking/geography";
import { useState } from "react";
import { offstreetClient } from "../../../../api/client";
import { FlowModalTitle as ModalTitle } from "../../../overlays/FlowModal/FlowModalTitle";
import { ResponsiveOverlay } from "../../../overlays/ResponsiveOverlay";
import { CreateVehicleModal } from "../../../vehicles/CreateVehicleModal";
import { EditVehicleModal } from "../../../vehicles/EditVehicleModal";
import { VehicleList } from "./VehicleList";
import { type ParkerVehicle, vehicleKey } from "./parker-vehicle";

type SelectedFormVehicle = {
	id?: number;
	plate: string;
	state: string;
};

type SelectVehiclesModalProps = {
	isOpen: boolean;
	maxVehicles: number;
	selectedVehicles: SelectedFormVehicle[];
	country: CountryValue;
	defaultVehicleState: string;
	description?: string;
	onClose: () => void;
	onConfirm: (vehicles: ParkerVehicle[]) => void;
	onVehicleEdited: (vehicle: ParkerVehicle) => void;
	onVehicleRemoved: (vehicleId: number) => void;
};

const toParkerVehicle = (vehicle: SelectedFormVehicle, fetchedByKey: Map<string, ParkerVehicle>): ParkerVehicle =>
	fetchedByKey.get(vehicleKey(vehicle)) ?? {
		id: vehicle.id ?? 0,
		plate: vehicle.plate,
		state: vehicle.state,
		nickname: null,
		isDefault: false,
	};

export const SelectVehiclesModal = ({
	isOpen,
	maxVehicles,
	selectedVehicles,
	country,
	defaultVehicleState,
	description,
	onClose,
	onConfirm,
	onVehicleEdited,
	onVehicleRemoved,
}: SelectVehiclesModalProps) => {
	const { t } = useLingui();
	const utils = offstreetClient.useUtils();
	const isSingleSelect = maxVehicles === 1;
	const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
	const [editingVehicle, setEditingVehicle] = useState<ParkerVehicle | undefined>(undefined);
	const isVehicleSubModalOpen = isCreateVehicleModalOpen || !!editingVehicle;
	const { data, error, isPending } = offstreetClient.parkers.getParkerVehicles.useQuery(undefined, {
		refetchOnWindowFocus: false,
		enabled: isOpen,
	});
	const parkerVehicles = data?.items ?? [];
	const [stagedVehicles, setStagedVehicles] = useState<ParkerVehicle[]>(() => {
		const fetchedByKey = new Map(parkerVehicles.map((vehicle) => [vehicleKey(vehicle), vehicle]));
		return selectedVehicles
			.filter((vehicle) => vehicle.plate)
			.map((vehicle) => toParkerVehicle(vehicle, fetchedByKey));
	});
	const handleStagedChange = ({ checked, option }: { checked: boolean; option: ParkerVehicle }) => {
		if (isSingleSelect) {
			setStagedVehicles(checked ? [option] : []);
			return;
		}
		setStagedVehicles((staged) =>
			checked ? [...staged, option] : staged.filter((vehicle) => vehicleKey(vehicle) !== vehicleKey(option)),
		);
	};
	return (
		<>
			<ResponsiveOverlay
				isOpen={isOpen && !isVehicleSubModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						onClose();
					}
				}}
				variant="fullScreen"
				title={
					<ModalTitle
						variant="fullScreen"
						icon={<VehicleIcon className="h-5 w-5" />}
						onClose={onClose}
						i18n={{
							closeButton: t`Close`,
						}}
					>
						{t`${plural(maxVehicles, {
							one: "Select Vehicle",
							other: "Select up to # vehicles",
						})}`}
					</ModalTitle>
				}
				description={description}
			>
				<div className="flex min-h-[60vh] flex-col gap-6 pt-2 pb-6 sm:min-h-0">
					<div className="flex-1 space-y-4">
						{error && (
							<Alert
								type="error"
								title={t`Something went wrong. Please try again.`}
								message={error.message}
							/>
						)}

						{isPending ? (
							<div className="space-y-4">
								{Array.from({
									length: Math.min(Math.max(maxVehicles, 1), 3),
								}).map((_, index) => (
									<Skeleton
										// biome-ignore lint/suspicious/noArrayIndexKey: This is a static array used for skeleton loading
										key={`select-vehicle-skeleton-${index}`}
										className="h-[78px] w-full rounded-lg"
									/>
								))}
							</div>
						) : (
							<VehicleList
								parkerVehicles={parkerVehicles}
								selectedVehicles={stagedVehicles}
								maxVehicles={maxVehicles}
								isSingleSelect={isSingleSelect}
								onChange={handleStagedChange}
								onEdit={(vehicle) => setEditingVehicle(vehicle)}
							/>
						)}

						<div className="flex justify-center pt-4">
							<button
								type="button"
								className="inline-flex items-center gap-2 font-medium text-accent underline"
								onClick={() => setIsCreateVehicleModalOpen(true)}
							>
								<AddIcon className="h-5 w-5" />
								{t`Add New Vehicle`}
							</button>
						</div>
					</div>

					<div className="-mx-4 -mb-6 sticky bottom-0 flex flex-col gap-2 border-neutral-faded border-t bg-white px-4 py-4 sm:relative sm:m-0 sm:border-0 sm:p-0">
						<Button
							type="button"
							className="w-full justify-center"
							isDisabled={isPending}
							onClick={() => {
								const fetchedByKey = new Map(
									parkerVehicles.map((vehicle) => [vehicleKey(vehicle), vehicle]),
								);
								onConfirm(
									stagedVehicles.map((vehicle) => fetchedByKey.get(vehicleKey(vehicle)) ?? vehicle),
								);
								onClose();
							}}
						>
							{t`${plural(maxVehicles, {
								one: "Select Vehicle",
								other: "Select Vehicles",
							})}`}
						</Button>
						<Button type="button" intent="secondary" className="w-full justify-center" onClick={onClose}>
							{t`Cancel`}
						</Button>
					</div>
				</div>
			</ResponsiveOverlay>

			<CreateVehicleModal
				isOpen={isCreateVehicleModalOpen}
				shouldDefault={parkerVehicles.length === 0}
				country={country}
				defaultState={defaultVehicleState}
				onSuccess={async (vehicle) => {
					setStagedVehicles((staged) => {
						if (isSingleSelect) {
							return [vehicle];
						}
						return staged.length < maxVehicles ? [...staged, vehicle] : staged;
					});
					await utils.parkers.getParkerVehicles.invalidate();
					setIsCreateVehicleModalOpen(false);
				}}
				onClose={() => setIsCreateVehicleModalOpen(false)}
			/>

			<EditVehicleModal
				isOpen={!!editingVehicle}
				vehicle={editingVehicle}
				country={country}
				onEdit={async (vehicle) => {
					setStagedVehicles((staged) =>
						staged.map((stagedVehicle) => (stagedVehicle.id === vehicle.id ? vehicle : stagedVehicle)),
					);
					onVehicleEdited(vehicle);
					await utils.parkers.getParkerVehicles.invalidate();
					setEditingVehicle(undefined);
				}}
				onRemove={async (payload) => {
					setStagedVehicles((staged) => staged.filter((vehicle) => vehicle.id !== payload.vehicleId));
					onVehicleRemoved(payload.vehicleId);
					await utils.parkers.getParkerVehicles.invalidate();
					setEditingVehicle(undefined);
				}}
				onClose={() => setEditingVehicle(undefined)}
			/>
		</>
	);
};
